// src/js/widgets/manager-residency-requests/composables/useRequestData.ts

import { ref, onMounted } from 'vue';
import {
  type Request,
  type UpdateStatusPayload,
  type ApiResponse,
  type RawApplicationDetails,
  type RawApplication,
  type RawApplicationsResponse,
  type PermissionFormData,
  FiltersListResponse,
} from '../types';
import { getApiUrl } from '@/js/utils/getApiUrl';
import { submitFormWithCaptcha } from '@/js/utils/submitFormWithCaptcha';
import { applyNotificationsSidebarBadge } from '@/js/utils/notifications-sidebar-badge';

async function fetchGet<T>(url: string): Promise<ApiResponse<T>> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  const text = await response.text();
  try {
    return JSON.parse(text);
  } catch {
    throw new Error('Сервер вернул недопустимый формат ответа');
  }
}

export function useRequestData() {
  const requests = ref<{
    data: RawApplication[];
    pagination: { totalCount: number; pageSize: number; currentPage: number; pageCount: number };
    newItemsCount: number;
  }>({
    data: [],
    pagination: { totalCount: 0, pageSize: 1, currentPage: 1, pageCount: 1 },
    newItemsCount: 0,
  });
  const loading = ref(false);
  const error = ref<string | null>(null);

  const loadRequests = async (
    params: {
      page?: number;
      filter?: Partial<Record<keyof FiltersListResponse, string>> & {
        dateFrom?: string | null;
        dateTo?: string | null;
      };
      search?: string;
    } = { page: 1 },
  ) => {
    loading.value = true;
    error.value = null;

    try {
      const query = new URLSearchParams();

      query.append('nav-report', `page-${params.page ?? 1}`);

      if (params?.filter?.status) {
        query.append('status', params.filter.status);
      }

      if (params?.filter?.businessArea) {
        query.append('businessArea', params.filter.businessArea);
      }

      if (params?.filter?.cluster) {
        query.append('cluster', params.filter.cluster);
      }

      if (params?.filter?.rentalProperty) {
        query.append('rentalProperty', params.filter.rentalProperty);
      }

      if (params?.filter?.educationType) {
        query.append('typeEducation', params.filter.educationType);
      }

      if (params?.filter?.dateFrom) {
        query.append('dateFrom', params.filter.dateFrom);
      }

      if (params?.filter?.dateTo) {
        query.append('dateTo', params.filter.dateTo);
      }

      if (params?.search?.trim()) {
        query.append('q', params.search.trim());
      }

      const url = getApiUrl(`/api/personal/application/?${query.toString()}`);

      const result = await fetchGet<RawApplicationsResponse>(url);

      if (result.status !== 'success') {
        throw new Error('Ошибка запроса списка заявок');
      }

      applyNotificationsSidebarBadge(document, result.msg.countNew);
      const applications: RawApplication[] = Object.entries(result.msg.items).map(
        ([, value]) => value as RawApplication,
      );
      const pagination = result.msg.nav;
      const newItemsCount = result.msg.countNew;

      const data: RawApplication[] = applications.map((item) => {
        return {
          id: item.id,
          createdAt: item.createdAt || '',
          fio: item?.fio || '',
          companyName: item?.companyName || '',
          status: item?.status || '',
          ...(Object.prototype.hasOwnProperty.call(item, 'email') && {
            email: item.email,
          }),
          ...(Object.prototype.hasOwnProperty.call(item, 'phone') && {
            phone: item.phone,
          }),
          ...(Object.prototype.hasOwnProperty.call(item, 'businessArea') && {
            businessArea: item.businessArea,
          }),
          ...(Object.prototype.hasOwnProperty.call(item, 'rentalProperty') && {
            rentalProperty: item.rentalProperty,
          }),
          ...(Object.prototype.hasOwnProperty.call(item, 'dateEvent') && {
            dateEvent: item.dateEvent,
          }),
          ...(Object.prototype.hasOwnProperty.call(item, 'educationType') && {
            educationType: item.educationType,
          }),
        };
      });

      data.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      requests.value = { data, pagination, newItemsCount };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Неизвестная ошибка';
      error.value = message;
      console.error('Ошибка загрузки заявок:', err);
    } finally {
      loading.value = false;
    }
  };

  const loadRequestDetails = async (id: string): Promise<Request | null> => {
    try {
      const url = getApiUrl(`/api/personal/application/${id}/`);
      const result = await fetchGet<RawApplicationDetails>(url);

      if (result.status !== 'success') {
        throw new Error(`Ошибка запроса заявки ${id}`);
      }

      const raw = result.msg;
      if (!raw) {
        throw new Error('Данные заявки отсутствуют');
      }

      const history =
        raw?.historyUpdate?.length && raw?.historyUpdate?.length > 0
          ? raw.historyUpdate
              .slice()
              .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
          : [];

      const request: Request = {
        id: raw?.id,
        createdAt: raw?.createdAt || '',
        ...(Object.prototype.hasOwnProperty.call(raw, 'businessArea') && {
          businessArea: raw.businessArea,
        }),
        ...(Object.prototype.hasOwnProperty.call(raw, 'areaActivity') && {
          areaActivity: raw.areaActivity,
        }),
        companyName: raw?.companyName || '',
        ...(Object.prototype.hasOwnProperty.call(raw, 'cluster') && {
          cluster: raw.cluster,
        }),
        ...(Object.prototype.hasOwnProperty.call(raw, 'projectDescription') && {
          projectDescription: raw.projectDescription,
        }),
        ...(Object.prototype.hasOwnProperty.call(raw, 'rentalProperty') && {
          premises: raw.rentalProperty,
        }),
        ...(Object.prototype.hasOwnProperty.call(raw, 'dateEvent') && {
          dateEvent: raw.dateEvent,
        }),
        ...(Object.prototype.hasOwnProperty.call(raw, 'timeEvent') && {
          timeEvent: raw.timeEvent,
        }),
        ...(Object.prototype.hasOwnProperty.call(raw, 'durationEvent') && {
          durationEvent: raw.durationEvent,
        }),
        ...(Object.prototype.hasOwnProperty.call(raw, 'format') && {
          format: raw.format,
        }),
        ...(Object.prototype.hasOwnProperty.call(raw, 'countParticipants') && {
          countParticipants: raw.countParticipants,
        }),
        ...(Object.prototype.hasOwnProperty.call(raw, 'additionalServices') && {
          additionalServices: raw.additionalServices,
        }),
        ...(Object.prototype.hasOwnProperty.call(raw, 'wishes') && {
          wishes: raw.wishes,
        }),
        ...(Object.prototype.hasOwnProperty.call(raw, 'legalForm') && { legalForm: raw.legalForm }),
        ...(Object.prototype.hasOwnProperty.call(raw, 'programDescription') && {
          programDescription: raw.programDescription,
        }),
        ...(Object.prototype.hasOwnProperty.call(raw, 'shortNameCompany') && {
          shortNameCompany: raw.shortNameCompany,
        }),
        ...(Object.prototype.hasOwnProperty.call(raw, 'address') && { address: raw.address }),
        ...(Object.prototype.hasOwnProperty.call(raw, 'registrationDate') && {
          registrationDate: raw.registrationDate,
        }),
        ...(Object.prototype.hasOwnProperty.call(raw, 'registrationAuthority') && {
          registrationAuthority: raw.registrationAuthority,
        }),
        ...(Object.prototype.hasOwnProperty.call(raw, 'educationType') && {
          educationType: raw.educationType,
        }),
        ...(Object.prototype.hasOwnProperty.call(raw, 'implementationForm') && {
          implementationForm: raw.implementationForm,
        }),
        fio: raw?.fio || '',
        ...(Object.prototype.hasOwnProperty.call(raw, 'workPosition') && {
          workPosition: raw.workPosition,
        }),
        phone: raw?.phone || '',
        email: raw?.email || '',
        ...(Object.prototype.hasOwnProperty.call(raw, 'files') && {
          files: raw.files,
        }),
        adminMessage: raw?.adminMessage || '',

        historyUpdate: history,
        status: raw?.status,
        statuses: raw?.statuses,
        inn: raw?.inn || '',
        ...(Object.prototype.hasOwnProperty.call(raw, 'ogrn') && {
          ogrn: raw.ogrn,
        }),
        legalAddress: raw?.legalAddress || '',
      };

      return request;
    } catch (err) {
      console.error('Ошибка загрузки деталей:', err);
      return null;
    }
  };

  const updateStatus = async (id: string, payload: UpdateStatusPayload) => {
    loading.value = true;
    error.value = null;

    try {
      const body: Record<string, string> = {};

      if (payload?.status?.id) {
        body.status = payload.status.id;
      }

      if (payload?.admin_comment) {
        body.admin_comment = payload.admin_comment;
      }

      const response = await fetch(getApiUrl(`/api/personal/application/${id}/`), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const text = await response.text();
      let result;
      try {
        result = JSON.parse(text);
      } catch {
        throw new Error('Сервер вернул недопустимый формат');
      }

      if (result.status !== 'success') {
        throw new Error(result.msg || 'Не удалось обновить статус');
      }

      const request = requests.value.data.find((r) => r.id === id);
      if (request && payload?.status?.id === '4' && request.status.id !== '4') {
        const newCount = requests.value.newItemsCount + 1;

        applyNotificationsSidebarBadge(document, newCount);
        requests.value = { ...requests.value, newItemsCount: newCount };
      }
      if (request && payload?.status?.id !== '4' && request.status.id === '4') {
        const newCount = requests.value.newItemsCount - 1;
        applyNotificationsSidebarBadge(document, newCount);
        requests.value = { ...requests.value, newItemsCount: newCount };
      }
      if (request && payload?.status) {
        request.status = payload.status;
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Неизвестная ошибка';
      error.value = message;
      console.error('Ошибка обновления статуса:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const sendReply = async (id: string, payload: PermissionFormData) => {
    loading.value = true;
    error.value = null;

    try {
      const response = await submitFormWithCaptcha({
        endpoint: `/api/personal/application/${id}/`,
        buildFormData: () => {
          const body = new FormData();
          if (payload?.comment) {
            body.append('comment', payload.comment);
          }
          if (payload?.files?.length > 0) {
            // Добавляем файлы с правильным ключом
            payload.files.forEach((file) => {
              body.append('replyAttachments[]', file);
            });
          }

          return body;
        },
      });

      if (!response.ok) {
        throw new Error(`Ошибка отправки ответа`);
      }
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Неизвестная ошибка';
      error.value = message;
      console.error('Ошибка отправки ответа:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const exportRequests = async (params: {
    filter?: Partial<Record<keyof FiltersListResponse, string>> & {
      dateFrom?: string | null;
      dateTo?: string | null;
    };
    search?: string;
  }) => {
    loading.value = true;
    error.value = null;

    try {
      const query = new URLSearchParams();

      if (params?.filter?.status) {
        query.append('status', params.filter.status);
      }

      if (params?.filter?.businessArea) {
        query.append('businessArea', params.filter.businessArea);
      }

      if (params?.filter?.cluster) {
        query.append('cluster', params.filter.cluster);
      }

      if (params?.filter?.rentalProperty) {
        query.append('rentalProperty', params.filter.rentalProperty);
      }

      if (params?.filter?.educationType) {
        query.append('typeEducation', params.filter.educationType);
      }

      if (params?.filter?.dateFrom) {
        query.append('dateFrom', params.filter.dateFrom);
      }

      if (params?.filter?.dateTo) {
        query.append('dateTo', params.filter.dateTo);
      }

      if (params?.search?.trim()) {
        query.append('q', params.search.trim());
      }

      const url = getApiUrl(`/api/personal/application/export-xlsx/?${query.toString()}`);

      const response = await fetch(url, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Ошибка экспорта заявок');
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get('content-disposition');
      const rawName = contentDisposition?.match(/filename="(.+)"/)?.[1] ?? 'export.xlsx';
      const bytes = Uint8Array.from(rawName, (c) => c.charCodeAt(0));
      const fileName = new TextDecoder('utf-8').decode(bytes);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = objectUrl;
      link.download = fileName;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Неизвестная ошибка';

      error.value = message;
      console.error('Ошибка экспорта заявок:', err);
    } finally {
      loading.value = false;
    }
  };

  onMounted(() => {
    loadRequests();
  });

  return {
    requests,
    loading,
    error,
    loadRequests,
    loadRequestDetails,
    updateStatus,
    sendReply,
    exportRequests,
  };
}
